import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Shared "What's New" logic used by both frontends (web-user + web-admin) so the dialog behaves identically
 * in each. Presentation is left to each app (its own modal chrome + styling); this hook owns the data +
 * open/close behaviour. The data calls are injected (dependency injection) so this package stays decoupled
 * from `@keydials/api-client`.
 */

export interface WhatsNewItem {
  /** "New" | "Improved" | "Fixed". */
  category: string;
  description: string;
}

export interface WhatsNewRelease {
  key: string;
  title: string;
  /** ISO date "yyyy-MM-dd". */
  publishedOn: string;
  items: WhatsNewItem[];
  /** False for a release only platform admins can see. Only platform admins are ever served one. */
  isPublic?: boolean;
}

export interface UseWhatsNewOptions {
  /** Fetch the caller's unread releases (already filtered to this app + undismissed by the API). */
  listUnread: () => Promise<WhatsNewRelease[]>;
  /** Record "Don't show this again" for one release. */
  dismiss: (key: string) => Promise<void>;
  /** Platform admins: make a release public or admin-only. Omit where the caller can't. */
  setPublic?: (key: string, isPublic: boolean) => Promise<void>;
}

export interface UseWhatsNewResult {
  /** The unread releases, newest first. Empty until loaded / when there is nothing new. */
  releases: WhatsNewRelease[];
  hasReleases: boolean;
  isOpen: boolean;
  /** Manually (re)open the dialog — e.g. from a header button. */
  open: () => void;
  /** Close the dialog. When {@link dontShowAgain} is true, every shown release is dismissed (fire-and-forget)
   * so it won't return on the next load. */
  close: (dontShowAgain?: boolean) => void;
  /** Make one release public or admin-only: updated here at once, put back if the save fails (and rethrows). */
  setPublic: (key: string, isPublic: boolean) => Promise<void>;
}

/** The three change buckets, in display order. */
export const WHATSNEW_CATEGORY_ORDER = ["New", "Improved", "Fixed"] as const;
export type WhatsNewCategory = (typeof WHATSNEW_CATEGORY_ORDER)[number];

/** Group a release's items by category, in canonical order, dropping empty groups. */
export function groupByCategory(release: WhatsNewRelease): { category: WhatsNewCategory; items: WhatsNewItem[] }[] {
  return WHATSNEW_CATEGORY_ORDER.map((category) => ({
    category,
    items: release.items.filter((i) => i.category?.toLowerCase() === category.toLowerCase()),
  })).filter((g) => g.items.length > 0);
}

/**
 * Group releases by their `publishedOn` date, newest date first, preserving each date's incoming order.
 * Used by the "all updates" history view to render one collapsible section per date. Generic over the release
 * shape so it works for both the unread releases and the richer history entries (which carry `dismissed`).
 * ISO "yyyy-MM-dd" strings sort lexicographically, so a plain string compare gives correct date ordering.
 */
export function groupByDate<T extends { publishedOn: string }>(releases: readonly T[]): { date: string; releases: T[] }[] {
  const byDate = new Map<string, T[]>();
  for (const release of releases) {
    const bucket = byDate.get(release.publishedOn);
    if (bucket) bucket.push(release);
    else byDate.set(release.publishedOn, [release]);
  }
  return [...byDate.entries()]
    .sort(([a], [b]) => (a < b ? 1 : a > b ? -1 : 0))
    .map(([date, group]) => ({ date, releases: group }));
}

export function useWhatsNew({ listUnread, dismiss, setPublic: setPublicOption }: UseWhatsNewOptions): UseWhatsNewResult {
  const [releases, setReleases] = useState<WhatsNewRelease[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  // Keep the latest injected callbacks in refs so the load effect runs exactly once (on mount) yet always
  // calls the current functions — the apps may pass fresh closures each render.
  const listUnreadRef = useRef(listUnread);
  const dismissRef = useRef(dismiss);
  const setPublicRef = useRef(setPublicOption);
  listUnreadRef.current = listUnread;
  dismissRef.current = dismiss;
  setPublicRef.current = setPublicOption;

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const items = await listUnreadRef.current();
        if (!active) return;
        setReleases(items);
        // Auto-open once per app load when there's something new; if the user closes it, it stays closed for
        // the rest of this load (a full reload re-runs this and re-opens only what's still unread server-side).
        if (items.length > 0) {
          setIsOpen(true);
        }
      } catch {
        // Best-effort: on failure the dialog simply doesn't appear.
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const open = useCallback(() => setIsOpen(true), []);

  const close = useCallback(
    (dontShowAgain = false) => {
      setIsOpen(false);
      if (dontShowAgain) {
        const dismissed = releases;
        setReleases([]); // won't reappear via the header button this session either
        for (const release of dismissed) {
          void dismissRef.current(release.key).catch(() => {
            /* fire-and-forget — a failed dismiss just means it shows again next load */
          });
        }
      }
    },
    [releases],
  );

  const setPublic = useCallback(async (key: string, isPublic: boolean) => {
    const save = setPublicRef.current;
    if (!save) return;
    const flip = (value: boolean) =>
      setReleases((current) => current.map((r) => (r.key === key ? { ...r, isPublic: value } : r)));
    flip(isPublic);
    try {
      await save(key, isPublic);
    } catch (e) {
      flip(!isPublic);
      throw e;
    }
  }, []);

  return { releases, hasReleases: releases.length > 0, isOpen, open, close, setPublic };
}
